import { useState } from "react";
import { Archive, Trash2, ArchiveRestore, Lock } from "lucide-react";
import { tokenStore, isSurfaceLive } from "./api/platformClient";
import { ArchiveTenantModal } from "./ArchiveTenantModal";
import { DeleteTestTenantFlow } from "./DeleteTestTenantFlow";

// Lifecycle Actions card — Phase 3D. Mounted on the Tenant Detail Overview tab.
// Visibility model (three states):
//   1) live=true  → active card with working buttons (current upstream supports lifecycle writes)
//   2) live=false AND dark=true → dark/disabled card with a small operator explanation
//      (used when ShelfLife production hasn't shipped Phase 3D yet; we want
//       operators to see that the surface exists but isn't enabled).
//   3) live=false AND dark=false → no card (true hidden state, e.g. mock/demo mode)
// ShelfLife is the authoritative role/flag check on every write regardless.
export const LifecycleActionsCard = ({ company, onArchived, onUnarchived, onDeleted }) => {
  const live = isSurfaceLive("TENANT_LIFECYCLE");
  const dark = process.env.REACT_APP_TENANT_LIFECYCLE_DARK === "true";
  const user = tokenStore.getUser();
  const isSuperadmin = user && user.role === "platform_superadmin";
  const [activeModal, setActiveModal] = useState(null); // archive | delete | null

  if (!company || !company.id) return null;
  if (!live && !dark) return null;

  // Dark mode — operator sees the surface but cannot act.
  if (!live && dark) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="lifecycle-actions-card-dark">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-start gap-2">
            <Lock className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-slate-700">Lifecycle actions</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Lifecycle actions are not enabled in production yet.
              </p>
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 bg-white border border-slate-300 rounded-full px-2 py-0.5">
            Dark
          </span>
        </div>
        <div className="flex flex-wrap gap-2 opacity-50 pointer-events-none select-none">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-500 text-sm font-medium">
            <Archive className="h-4 w-4" />
            Archive tenant
          </span>
          {isSuperadmin && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-500 text-sm font-medium">
              <Trash2 className="h-4 w-4" />
              Delete test tenant
            </span>
          )}
        </div>
      </div>
    );
  }

  const status = String(company.status || "").toLowerCase();
  const isArchived = status === "archived";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="lifecycle-actions-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Lifecycle actions</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {isArchived
              ? "This tenant is archived. Users cannot sign in."
              : "Reversible archive, or permanent test-tenant deletion."}
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
          Live · Phase 3D
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="lifecycle-archive-btn"
          onClick={() => setActiveModal("archive")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm font-medium hover:bg-amber-100"
        >
          {isArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          {isArchived ? "Unarchive tenant" : "Archive tenant"}
        </button>

        {isSuperadmin && (
          <button
            type="button"
            data-testid="lifecycle-delete-test-btn"
            onClick={() => setActiveModal("delete")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-300 bg-rose-50 text-rose-800 text-sm font-medium hover:bg-rose-100"
          >
            <Trash2 className="h-4 w-4" />
            Delete test tenant
          </button>
        )}
      </div>

      {!isSuperadmin && (
        <p className="text-[11px] text-slate-400 mt-3" data-testid="lifecycle-superadmin-only-note">
          Permanent deletion requires platform_superadmin.
        </p>
      )}

      {activeModal === "archive" && (
        <ArchiveTenantModal
          companyId={company.id}
          companyName={company.name}
          onClose={() => setActiveModal(null)}
          onArchived={() => { setActiveModal(null); onArchived && onArchived(); }}
        />
      )}

      {activeModal === "delete" && (
        <DeleteTestTenantFlow
          companyId={company.id}
          companyName={company.name}
          companyCode={company.code || company.slug || company.id}
          onClose={() => setActiveModal(null)}
          onDeleted={() => { setActiveModal(null); onDeleted && onDeleted(); }}
        />
      )}
    </div>
  );
};
